import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ORIGIN_META, ORIGIN_ORDER } from "@/lib/provenance";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Loader2, Search, ChevronDown, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Row colour-coding by identified data source.
const ROW_TONE = {
  sample: "border-l-rose-400 bg-rose-50/30",
  unverified: "border-l-amber-400 bg-amber-50/30",
  imported: "border-l-blue-400 bg-blue-50/30",
  synced: "border-l-teal-400 bg-teal-50/30",
  manual: "border-l-emerald-400 bg-emerald-50/30",
  calculated: "border-l-violet-400 bg-violet-50/30",
};

const PAGE_SIZES = [10, 25, 50, 100];

export function ProvenanceRecordsDialog({ dataset, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [origin, setOrigin] = useState("all");
  const [colFilters, setColFilters] = useState({});
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [retagging, setRetagging] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [bulkOrigin, setBulkOrigin] = useState("synced");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  // Debounced copy of the per-column filters so typing doesn't spam the API.
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const timer = useRef(null);

  // Reset state whenever a new dataset is opened. Land on the rows that need
  // attention — unverified (or sample) — so "View records" is actionable.
  useEffect(() => {
    if (open) {
      const c = dataset?.counts || {};
      const initialOrigin = (c.unverified > 0) ? "unverified" : (c.sample > 0 ? "sample" : "all");
      setData(null); setPage(1); setPageSize(25); setOrigin(initialOrigin);
      setColFilters({}); setDebouncedFilters({}); setSortBy(null); setSortDir("asc");
      setSelectedIds(new Set()); setSelectAllMatching(false); setBulkOrigin("synced");
    }
  }, [open, dataset?.key]);

  // Any change to the filtered set invalidates the current selection.
  const clearSelection = () => { setSelectedIds(new Set()); setSelectAllMatching(false); };
  useEffect(() => { clearSelection(); }, [debouncedFilters, origin, page, pageSize]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { setDebouncedFilters(colFilters); setPage(1); }, 350);
    return () => clearTimeout(timer.current);
  }, [colFilters]);

  // Active (non-empty) column filters sent to the API / used by bulk actions.
  const activeFilters = useMemo(
    () => Object.fromEntries(Object.entries(debouncedFilters).filter(([, v]) => v && v.trim())),
    [debouncedFilters]);

  useEffect(() => {
    if (!open || !dataset?.key) return;
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (origin !== "all") params.set("origin", origin);
    if (sortBy) { params.set("sort_by", sortBy); params.set("sort_dir", sortDir); }
    if (Object.keys(activeFilters).length) params.set("filters", JSON.stringify(activeFilters));
    setLoading(true);
    api.get(`/provenance/records/${dataset.key}?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, dataset?.key, page, pageSize, origin, sortBy, sortDir, activeFilters, refresh]);

  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  const toggleSort = (key) => {
    if (sortBy === key) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); }
    else { setSortBy(key); setSortDir("asc"); }
    setPage(1);
  };

  const pageIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const selectedCount = selectAllMatching ? total : selectedIds.size;
  const hasSelection = selectedCount > 0;

  const toggleRow = (id) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const togglePage = () => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const applyBulk = async () => {
    setBulkBusy(true);
    try {
      const payload = selectAllMatching
        ? { dataset_key: dataset.key, origin: bulkOrigin, all_matching: true,
            filters: activeFilters, origin_filter: origin !== "all" ? origin : undefined }
        : { dataset_key: dataset.key, origin: bulkOrigin, record_ids: Array.from(selectedIds) };
      const r = await api.post("/provenance/retag-bulk", payload);
      toast.success(`${r.data.updated} record(s) re-tagged as ${ORIGIN_META[bulkOrigin].label}`);
      clearSelection();
      setRefresh((n) => n + 1);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk re-tag failed");
    } finally { setBulkBusy(false); }
  };

  const retag = async (row, newOrigin) => {
    if (newOrigin === row.data_origin) return;
    setRetagging(row.id);
    try {
      await api.post("/provenance/retag", { dataset_key: dataset.key, record_id: row.id, origin: newOrigin });
      setData((d) => ({ ...d, rows: d.rows.map((r) => r.id === row.id ? { ...r, data_origin: newOrigin } : r) }));
      toast.success(`Re-tagged as ${ORIGIN_META[newOrigin].label}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-tag failed");
    } finally { setRetagging(null); }
  };

  const rangeLabel = useMemo(() => {
    if (!total) return "0 records";
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`;
  }, [page, pageSize, total]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[94vw] w-[94vw] h-[88vh] flex flex-col p-0 gap-0" data-testid="provenance-records-dialog">
        <DialogHeader className="px-5 py-3.5 border-b border-slate-100 shrink-0">
          <DialogTitle className="font-display flex items-center gap-3">
            {dataset?.label}
            <span className="text-[11px] font-mono font-normal text-slate-400">{dataset?.collection}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Controls */}
        <div className="px-5 py-2.5 flex items-center gap-3 flex-wrap border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-400">Data source</span>
            <select data-testid="records-origin-filter" value={origin}
              onChange={(e) => { setOrigin(e.target.value); setPage(1); }}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white">
              <option value="all">All sources</option>
              {ORIGIN_ORDER.map((o) => <option key={o} value={o}>{ORIGIN_META[o].label}</option>)}
            </select>
          </div>
          {total > 0 && !selectAllMatching && (
            <button data-testid="records-select-all-direct" type="button"
              onClick={() => setSelectAllMatching(true)}
              className="h-8 inline-flex items-center gap-1.5 text-xs px-3 rounded-md border border-[#00334C]/30 bg-[#00334C]/5 text-[#00334C] font-medium hover:bg-[#00334C]/10">
              <ShieldCheck className="w-3.5 h-3.5" /> Select all {total.toLocaleString()}
            </button>
          )}
          {selectAllMatching && (
            <span className="text-xs text-[#00334C] font-medium inline-flex items-center gap-2">
              All {total.toLocaleString()} selected
              <button data-testid="records-select-all-cancel" onClick={clearSelection}
                className="underline underline-offset-2 opacity-80 hover:opacity-100">clear</button>
            </span>
          )}
          <div className="ml-auto flex items-center gap-4 flex-wrap text-[11px]">
            {ORIGIN_ORDER.map((o) => (
              <span key={o} className="inline-flex items-center gap-1 text-slate-500">
                <span className={`w-2 h-2 rounded-full ${ORIGIN_META[o].dot}`} />{ORIGIN_META[o].label}
              </span>
            ))}
          </div>
        </div>

        {/* Bulk action bar */}
        {hasSelection && (
          <div data-testid="records-bulk-bar" className="px-5 py-2.5 flex items-center gap-3 flex-wrap bg-[#00334C] text-white shrink-0">
            <span className="text-xs font-medium" data-testid="records-bulk-count">
              {selectedCount.toLocaleString()} record{selectedCount === 1 ? "" : "s"} selected
            </span>
            {!selectAllMatching && allPageSelected && total > rows.length && (
              <button data-testid="records-select-all-matching" onClick={() => setSelectAllMatching(true)}
                className="text-xs underline underline-offset-2 hover:opacity-80">
                Select all {total.toLocaleString()} matching the current filters
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] opacity-80">Set data source to</span>
              <select data-testid="records-bulk-origin" value={bulkOrigin} onChange={(e) => setBulkOrigin(e.target.value)}
                className="h-8 text-xs rounded-md px-2 bg-white text-slate-800 border border-white/20">
                {ORIGIN_ORDER.map((o) => <option key={o} value={o}>{ORIGIN_META[o].label}</option>)}
              </select>
              <button data-testid="records-bulk-apply" onClick={applyBulk} disabled={bulkBusy}
                className="h-8 inline-flex items-center gap-1.5 text-xs px-3 rounded-md bg-white text-[#00334C] font-medium hover:opacity-90 disabled:opacity-50">
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Apply
              </button>
              <button data-testid="records-bulk-clear" onClick={clearSelection}
                className="h-8 inline-flex items-center justify-center w-8 rounded-md hover:bg-white/10">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto relative">
          {loading && (
            <div className="absolute inset-0 bg-white/60 z-20 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          )}
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2 w-9">
                  <input type="checkbox" data-testid="records-select-page" checked={allPageSelected}
                    onChange={togglePage} className="cursor-pointer accent-[#00334C]" />
                </th>
                <th className="text-left font-medium text-slate-500 px-3 py-2 whitespace-nowrap">Source</th>
                {columns.map((c) => (
                  <th key={c.key} className="text-left font-medium text-slate-600 px-3 py-2 whitespace-nowrap">
                    <button data-testid={`records-sort-${c.key}`} onClick={() => toggleSort(c.key)}
                      className="inline-flex items-center gap-1 hover:text-slate-900">
                      {c.label}
                      {sortBy === c.key && (sortDir === "asc"
                        ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </button>
                  </th>
                ))}
              </tr>
              <tr className="border-b border-slate-200 bg-white">
                <th className="px-3 py-1.5"></th>
                <th className="px-3 py-1.5"></th>
                {columns.map((c) => (
                  <th key={c.key} className="px-2 py-1.5">
                    <div className="relative">
                      <Search className="w-3 h-3 text-slate-300 absolute left-1.5 top-1/2 -translate-y-1/2" />
                      <input data-testid={`records-filter-${c.key}`}
                        value={colFilters[c.key] || ""}
                        onChange={(e) => setColFilters((f) => ({ ...f, [c.key]: e.target.value }))}
                        placeholder="filter…"
                        className="w-full min-w-[90px] h-7 pl-6 pr-2 text-[11px] font-normal border border-slate-200 rounded bg-white focus:border-slate-400 outline-none" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr><td colSpan={columns.length + 2} className="px-4 py-10 text-center text-slate-400">No records match these filters.</td></tr>
              ) : rows.map((r) => {
                const checked = selectAllMatching || selectedIds.has(r.id);
                return (
                <tr key={r.id} data-testid={`records-row-${r.id}`}
                  className={`border-b border-slate-100 border-l-4 ${checked ? "bg-sky-50" : ROW_TONE[r.data_origin] || "border-l-slate-300"}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox" data-testid={`records-select-${r.id}`} checked={checked}
                      disabled={selectAllMatching} onChange={() => toggleRow(r.id)}
                      className="cursor-pointer accent-[#00334C]" />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="relative inline-flex items-center">
                      <span className={`inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-full border text-[10px] font-medium ${ORIGIN_META[r.data_origin]?.tone || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        {retagging === r.id
                          ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                          : <span className={`w-1.5 h-1.5 rounded-full ${ORIGIN_META[r.data_origin]?.dot}`} />}
                        {ORIGIN_META[r.data_origin]?.label || r.data_origin}
                        <ChevronDown className="w-2.5 h-2.5 opacity-60" />
                      </span>
                      <select data-testid={`records-retag-${r.id}`} value={r.data_origin}
                        disabled={retagging === r.id}
                        onChange={(e) => retag(r, e.target.value)}
                        title="Re-tag this record's data source"
                        className="absolute inset-0 opacity-0 cursor-pointer">
                        {ORIGIN_ORDER.map((o) => <option key={o} value={o}>{ORIGIN_META[o].label}</option>)}
                      </select>
                    </div>
                  </td>
                  {columns.map((c) => {
                    const v = r.values[c.key];
                    const display = v === true ? "Yes" : v === false ? "No" : String(v ?? "");
                    return (
                      <td key={c.key} className="px-3 py-2 text-slate-700 max-w-[260px] truncate" title={display}>
                        {display || <span className="text-slate-300">—</span>}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-4 flex-wrap shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-400">Rows per page</span>
            <select data-testid="records-page-size" value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white">
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <span className="text-xs text-slate-500" data-testid="records-range">{rangeLabel}</span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="records-prev" disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-600 tabular-nums">Page {page} of {pages}</span>
            <button data-testid="records-next" disabled={page >= pages || loading}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
